import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import Table from "../../../core/common/dataTable/index";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import PageHeader from "../../../core/common/commonComponents/PageHeader";
import DeleteModal1 from "../../../core/modals/deleteModal1";
import { exportToCSV, exportToExcel, exportToPDF } from "../../../core/common/GlobalFunctions/exportTableData";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../core/data/redux/store";
import { filterData } from "../../../core/common/GlobalFunctions/tableFilters";
import EmployeeTdsContributionModal from "./modal/EmployeeTdsContributionModal";
import { fetchEmployeeTdsContributions, deleteEmployeeTdsContribution, createEmployeeTdsContribution } from "../../../core/data/redux/payroll/TDS/employeeTdsContribution/employeeTdsContributionAction";
import StatusFilterHeaderWithYearMonth from "../../../core/common/commonComponents/StatusFilterHeaderWithYearMonth";
import { Button, Drawer, Select, DatePicker, message } from "antd";
import dayjs from "dayjs";
import { fetchEmployeeSalaryStructures } from "../../../core/data/redux/payroll/payrollStructure/payrollStructure/employeeSalaryStructureActions";
import { fetchTdsSurchargeCessList } from "../../../core/data/redux/payroll/TDS/tdsSurchargeCess/tdsSurchargeCessActions";
import { fetchTdsTaxRegimes } from "../../../core/data/redux/payroll/TDS/tdsTaxRegime/tdsTaxRegimeActions";
import { fetchAllTaxes } from "../../../core/data/redux/payroll/TDS/taxes/taxActions";
import { useAppConfig } from "../../../customhooks/useAppConfig";
import { buildSlabsFromTaxes, calculateMonthlyTDSDynamic } from "./utils/tdsCalculator";
import { calculateMonthlyGhanaPAYE } from "./utils/ghanaPayeeCalculator";
import { fetchFinancialYearsByCompanyId } from "../../../core/data/redux/financialYear/financialYearActions";
import { fetchEarnings } from "../../../core/data/redux/payroll/payrollComponent/earning/earningActions";
import { decryptData } from "../../../core/utils/encryptUtils";
import { getEmployees } from "../../../core/data/redux/employee/employeeAction";
import { fetchTdsDeclarations } from "../../../core/data/redux/payroll/TDS/tdsDelcaration/tdsDeclarationAction";


const parseNum = (v: any) => (v == null ? 0 : Number(v));

const genderFromId = (id?: number | null) => {
    if (id === 1) return "Male";
    if (id === 2) return "Female";
    return "Both";
};

const parseAgeRange = (s?: string | null) => {
    if (!s) return { min: 0, max: Infinity };
    const m = String(s).match(/(\d+)\s*[\-–]\s*(\d+)/);
    if (!m) return { min: 0, max: Infinity };
    return { min: Number(m[1]) || 0, max: Number(m[2]) || Infinity };
};

const calcAgeYears = (dobISO?: string | null, asOfISO?: string) => {
    if (!dobISO) return 0;
    const base = asOfISO ? dayjs(asOfISO) : dayjs();
    return base.diff(dayjs(dobISO), "year");
};

// Filter taxes by regime + employee age + employee gender (same as modal)
function filterTaxesForEmployee(
    taxes: any[],
    selectedRegimeID: number | null,
    employeeAge: number,
    employeeGender: string
) {
    if (!Array.isArray(taxes) || !selectedRegimeID) return [];
    return (taxes || [])
        .filter((t: any) => String(t.status || "").toLowerCase() === "active")
        .filter((t: any) => {
            const byRegimeId = Number(t.regimeId) === Number(selectedRegimeID);
            const byTaxNameFallback = String(t.tax_name) === String(selectedRegimeID);
            if (!(byRegimeId || byTaxNameFallback)) return false;

            const taxGender = String(t.gender || "Both");
            const genderOk = taxGender === "Both" || taxGender === employeeGender;

            const { min, max } = parseAgeRange(t.age);
            const ageOk = employeeAge >= min && employeeAge <= max;

            return genderOk && ageOk;
        })
        .map((t: any) => ({
            ...t,
            _min: parseNum(t.min_amount),
            _max: Number(t.max_amount) === 0 ? Infinity : parseNum(t.max_amount),
        }))
        .sort((a: any, b: any) => a._min - b._min);
}



const EmployeeTdsContribution = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { items = [] } = useSelector((state: any) => state.TDS.employeeTDSContribution);
    const [selectedStatus, setSelectedStatus] = useState("All");
    const [selectedSort, setSelectedSort] = useState<any>("");
    const [tableKey, setTableKey] = useState(0);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [modalType, setModalType] = useState<'edit' | 'create' | ''>('');
    const [selectedRowData, setSelectedRowData] = useState<any>(null);
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear().toString();
    const currentMonth = currentDate.toLocaleString("default", { month: "long" });
    const { financialYearsByCompany } = useSelector((state: any) => state.financialYears);
    const companyId = useSelector((state: any) => state.companyDetails.activeCompany?.company_detail_id);
    const { earnings } = useSelector((state: any) => state.payroll.earning);
    const [bulkEarningHeadIds, setBulkEarningHeadIds] = useState<number[]>([]);
    const { employees = [] } = useSelector((s: any) => s.employee);
    const { declarations = [] } = useSelector((s: any) => s.TDS.tdsDeclaration);

    // Permission
    const encryptedPermissions = localStorage.getItem('currentPermissions');
    const permissions: string[] = Array.isArray(decryptData(encryptedPermissions || '')) ? decryptData(encryptedPermissions || '') : [];

    const canAdd =
        permissions.includes("Add") || permissions.includes("AllowAll");
    const canView =
        permissions.includes("View") || permissions.includes("AllowAll");
    const canEdit =
        permissions.includes("Edit") || permissions.includes("AllowAll");
    const canDelete =
        permissions.includes("Delete") || permissions.includes("AllowAll");
    const canExport =
        permissions.includes("Export") || permissions.includes("AllowAll");


    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [selectedMonth, setSelectedMonth] = useState(currentMonth);

    // inside component
    const config = useAppConfig();
    const { salaryStructures = [] } = useSelector((s: any) => s.payroll.salaryStructure);
    const { regimes = [] } = useSelector((s: any) => s.TDS.tdsTaxRegime);
    const { surchargeCessList = [] } = useSelector((s: any) => s.TDS.tdsSurchargeCess);
    const { taxes = [] } = useSelector((s: any) => s.accounting.taxes);
    const { user } = useSelector((s: any) => s.auth);

    // modal state
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkFY, setBulkFY] = useState<number | null>(null);
    const [bulkMonth, setBulkMonth] = useState<string>(""); // "YYYY-MM"
    const [bulkRegimeId, setBulkRegimeId] = useState<number | null>(null);
    const [bulkCessId, setBulkCessId] = useState<number | null>(null);
    const [bulkLoading, setBulkLoading] = useState(false);

    // fetch dependencies once
    useEffect(() => {
        dispatch(fetchEmployeeSalaryStructures());
        dispatch(fetchTdsSurchargeCessList());
        dispatch(fetchTdsTaxRegimes());
        dispatch(fetchAllTaxes());
    }, [dispatch]);

    useEffect(() => {
        dispatch(getEmployees());
        dispatch(fetchTdsDeclarations());
    }, [dispatch]);

    useEffect(() => {
        if (companyId) {
            dispatch(fetchFinancialYearsByCompanyId(companyId));
        }
    }, [dispatch, companyId]);

    // overlap helper: checks if structure [start_date, end_date] overlaps selected month
    const overlapsMonth = (startISO?: string, endISO?: string, ym?: string) => {
        if (!startISO || !ym) return false;
        const start = dayjs(startISO);                      // structure start
        const end = endISO ? dayjs(endISO) : dayjs("2999-12-31");
        const ymStart = dayjs(ym + "-01");
        const ymEnd = ymStart.endOf("month");
        return !(end.isBefore(ymStart, "day") || start.isAfter(ymEnd, "day"));
    };

    // slab finder (same logic as in your modal)
    const parseNum = (v: any) => (v == null ? 0 : Number(v));
    function findMatchingSlab(annualIncome: number, taxesArr: any[]) {
        if (!annualIncome || annualIncome <= 0) return null;
        const active = (taxesArr || [])
            .filter((t: any) => String(t.status || "").toLowerCase() === "active")
            .map((t: any) => ({
                tax_id: t.tax_id,
                min: parseNum(t.min_amount),
                max: Number(t.max_amount) === 0 ? Infinity : parseNum(t.max_amount),
                raw: t,
            }))
            .sort((a: any, b: any) => a.min - b.min);
        const match = active.find(s => annualIncome >= s.min && annualIncome <= s.max);
        return match?.raw || null;
    }


    const showModal = (type: 'edit' | 'create') => {
        setModalType(type);
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setModalType('');
        setIsModalVisible(false);
    };

    useEffect(() => {
        dispatch(fetchEmployeeTdsContributions());
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchEarnings());
    }, [dispatch])

    const filteredData = useMemo(() => {
        let filters: any = {};

        if (selectedStatus !== "All") filters.status = selectedStatus;

        let data: any = filterData(items, filters, "CreatedAt", "CreatedAt", selectedSort);

        if (selectedYear) {
            data = data.filter((item: any) => {
                const [year] = item.MonthYear?.split("-") || [];
                return year === selectedYear;
            });
        }
        if (selectedMonth) {
            const monthNumber = new Date(`${selectedMonth} 1, 2000`).getMonth() + 1;
            const formattedMonth = monthNumber.toString().padStart(2, "0");

            data = data.filter((item: any) => {
                const [, month] = item.MonthYear?.split("-") || [];
                return month === formattedMonth;
            });
        }

        return data;
    }, [items, selectedStatus, selectedSort, selectedYear, selectedMonth]);

    useEffect(() => {
        setTableKey((prevKey) => prevKey + 1);
    }, [dispatch, selectedStatus, selectedSort, items, selectedYear, selectedMonth]);

    const exportData = filteredData.map((item: any) => ({
        MonthYear: item.MonthYear || "N/A",
        EmployeeName: item.Name || "N/A",
        FinancialYearID: item.name || "N/A",
        ApplicableIncome: item.ApplicableIncome || 0,
        CalculatedTax: item.CalculatedTax || 0,
    }));

    const handleDelete = async (id: number) => {
        try {
            const result = await dispatch(deleteEmployeeTdsContribution(id));
            if (result.meta.requestStatus === 'fulfilled') {
                message.success('Employee TDS Contribution deleted successfully');
                setIsDeleteModalVisible(false);
                dispatch(fetchEmployeeTdsContributions());
            } else {
                message.error('Delete failed');
            }
        } catch (error) {
            message.error("Something went wrong");
        }
    };

    const columns = [
        {
            title: "Employee Name",
            dataIndex: "Name",
        },
        {
            title: "Financial Year",
            dataIndex: "name",
        },
        {
            title: "Year/Month",
            dataIndex: "MonthYear",
        },
        {
            title: " Projected Annual Applicable Income",
            dataIndex: "ApplicableIncome",
        },
        {
            title: " Actual Annual Applicable Income",
            dataIndex: "ActualIncome",
        },
        
        {
            title: "Monthly Calculated Tax",
            dataIndex: "CalculatedTax",
        },
        {
            title: "Action",
            render: (_: any, record: any) => {
                const today = new Date();
                const [year, month] = record.MonthYear?.split("-") || [];
                const recordDate = new Date(parseInt(year), parseInt(month) - 1);

                const currentMonthStart = new Date(today.getFullYear(), today.getMonth());

                const isDeleteDisabled = recordDate < currentMonthStart;

                return (
                    <div className="action-icon d-inline-flex">
                        {canEdit && (
                            <button
                                className="me-2"
                                disabled={isDeleteDisabled}
                                onClick={() => {
                                    showModal("edit");
                                    setSelectedRowData(record);
                                }}
                                title={isDeleteDisabled ? "Can't Edit past records" : "Edit"}
                            >
                                <i className="ti ti-edit" />
                            </button>
                        )}
                        {canDelete && (
                            <button
                                disabled={isDeleteDisabled}
                                onClick={() => {
                                    setSelectedRowData(record);
                                    setIsDeleteModalVisible(true);
                                }}
                                title={isDeleteDisabled ? "Can't delete past records" : "Delete"}
                            >
                                <i className="ti ti-trash" />
                            </button>
                        )}
                    </div>
                );
            },
        }
    ];
    // ---------------------------------------------------
// Listen for AI → Auto TDS trigger (LIKE PF)
// ---------------------------------------------------
useEffect(() => {
    const handler = (event: any) => {
        const {
            FinancialYearID,
            MonthYear,
            TaxRegimeID,
            SurchargeCessID,
            earning_head_ids,
        } = event.detail || {};

        // 1️⃣ Set state from AI
        setBulkFY(Number(FinancialYearID));
        setBulkMonth(MonthYear);               // "YYYY-MM"
        setBulkRegimeId(Number(TaxRegimeID));
        setBulkCessId(Number(SurchargeCessID));
        setBulkEarningHeadIds(
            (earning_head_ids || []).map((id: any) => Number(id))
        );

        // 2️⃣ Open drawer (same UX as PF)
        setBulkOpen(true);

        // 3️⃣ 🚀 Reuse SAME logic as manual "Process TDS"
        setTimeout(() => {
            // this is your existing button logic
            document
                .querySelector<HTMLButtonElement>(
                    'button[data-action="process-tds"]'
                )
                ?.click();
        }, 300);
    };

    window.addEventListener("tds-auto-process", handler);
    return () =>
        window.removeEventListener("tds-auto-process", handler);
}, []);


    return (
        <>
            <div className="page-wrapper">
                <div className="content">
                    <PageHeader
                        title="Employee TDS Contribution"
                        breadcrumbs={[
                            { label: <i className="ti ti-smart-home" />, path: all_routes.adminDashboard },
                            { label: "Payroll" },
                            { label: "Employee TDS Contribution", isActive: true },
                        ]}
                        listViewPath="#"
                        onExportCSV={canExport ? () => exportToCSV(exportData, "EmployeeTdsContribution") : undefined}
                        onExportExcel={canExport ? () => exportToExcel(exportData, "EmployeeTdsContribution") : undefined}
                        onExportPDF={canExport ? () => exportToPDF(exportData, "EmployeeTdsContribution") : undefined}
                        onAdd={canAdd ? () => showModal("create") : undefined}
                        addLabel="Add TDS Contribution"
                        exportEnabled={canExport}
                        addButtonEnabled={canAdd}
                        rightExtra={
                            <div className="d-flex align-items-center gap-2">
                                <Button
                                    type="default"
                                    onClick={() => setBulkOpen(true)}
                                    className="d-inline-flex align-items-center "
                                    style={{ paddingTop: '20px', paddingBottom: '20px' }}
                                >
                                    <i className="ti ti-robot me-1" />
                                    Auto Process TDS
                                </Button>
                                <CollapseHeader />
                            </div>
                        }
                    />

                    <div className="card">
                        <StatusFilterHeaderWithYearMonth
                            title="Employee TDS Contribution List"
                            selectedStatus={selectedStatus}
                            setSelectedStatus={setSelectedStatus}
                            selectedSort={selectedSort}
                            setSelectedSort={setSelectedSort}
                            selectedYear={selectedYear}
                            setSelectedYear={setSelectedYear}
                            selectedMonth={selectedMonth}
                            setSelectedMonth={setSelectedMonth}
                        />
                        <div className="card-body p-0">
                            <Table key={tableKey} dataSource={filteredData} columns={columns} Selection={false} />
                        </div>
                    </div>
                </div>
                <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
                    <p className="mb-0">2024 - 2025© hrmtree.</p>
                    <p>
                        Developed By {" "}
                        <Link to="#" className="text-primary">
                            Leadchain Systems
                        </Link>
                    </p>
                </div>
            </div>

            <EmployeeTdsContributionModal
                modalType={modalType}
                handleCancel={handleCancel}
                isModalVisible={isModalVisible}
                selectedRowData={selectedRowData}
            />

            <DeleteModal1
                isVisible={isDeleteModalVisible}
                onClose={() => setIsDeleteModalVisible(false)}
                onConfirm={() => selectedRowData?.EmployeeTaxMapID && handleDelete(selectedRowData?.EmployeeTaxMapID)}
            />
            {/* 🔥 Bulk Process Drawer */}
            <Drawer
                title="Auto Process TDS for All Employees"
                placement="right"
                width={520}
                onClose={() => setBulkOpen(false)}
                visible={bulkOpen}
            >
                <div className="row">
                    <div className="col-12 mb-3">
                        <label className="form-label">Financial Year <span className="text-danger">*</span></label>
                        <Select
                            className="w-100"
                            value={bulkFY}
                            onChange={setBulkFY}
                            placeholder="Select financial year"
                            options={(financialYearsByCompany || []).map((fy: any) => ({ value: fy.id, label: fy.name }))}
                            allowClear
                        />
                    </div>

                    <div className="col-12 mb-3">
                        <label className="form-label">Month & Year <span className="text-danger">*</span></label>
                        <DatePicker.MonthPicker
                            className="w-100"
                            value={bulkMonth ? dayjs(bulkMonth, "YYYY-MM") : null}
                            format="YYYY-MM"
                            onChange={(d) => setBulkMonth(d ? d.format("YYYY-MM") : "")}
                            placeholder="Select month"
                        />
                    </div>

                    <div className="col-12 mb-3">
                        <label className="form-label">Tax Regime <span className="text-danger">*</span></label>
                        <Select
                            className="w-100"
                            value={bulkRegimeId}
                            onChange={setBulkRegimeId}
                            placeholder="Select Tax Regime"
                            allowClear
                        >
                            {regimes?.map((reg: any) => (
                                <Select.Option key={reg.TaxRegimeID} value={reg.TaxRegimeID}>
                                    {reg.RegimeName} ({reg.FinancialYearName})
                                </Select.Option>
                            ))}
                        </Select>
                    </div>

                    <div className="col-12 mb-4">
                        <label className="form-label">Surcharge & Cess <span className="text-danger">*</span></label>
                        <Select
                            className="w-100"
                            value={bulkCessId}
                            onChange={setBulkCessId}
                            placeholder="Select Surcharge & Cess"
                            allowClear
                        >
                            {surchargeCessList?.map((sc: any) => (
                                <Select.Option key={sc.SurchargeCessID} value={sc.SurchargeCessID}>
                                    Cess {sc.HealthAndEducationCessRate_Percentage}%
                                    {Number(sc.SurchargeRate1_Percentage) > 0 && ` | S1: ${sc.SurchargeRate1_Percentage}%`}
                                    {Number(sc.SurchargeRate2_Percentage) > 0 && ` | S2: ${sc.SurchargeRate2_Percentage}%`}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                </div>
                <div className="col-12 mb-3">
                    <label className="form-label">
                        Earning Heads to include <span className="text-danger">*</span>
                    </label>
                    <Select
                        mode="multiple"
                        className="w-100 custom-select"
                        value={bulkEarningHeadIds}
                        onChange={(vals) => setBulkEarningHeadIds(vals as number[])}
                        placeholder="Pick earning heads (e.g., Basic, HRA, TA)"
                        options={(earnings || []).map((h: any) => ({ value: h.Id, label: h.Name }))}
                    />
                    <small className="text-muted">
                        Only these components will be considered for Applicable Income.
                    </small>
                </div>

                <div className="d-flex justify-content-end">
                    <Button className="me-2" onClick={() => setBulkOpen(false)}>Cancel</Button>
                    <Button
                        type="primary"
                        data-action="process-tds"
                        loading={bulkLoading}
                        onClick={async () => {
                            if (!bulkFY || !bulkMonth || !bulkRegimeId || !bulkCessId) {
                                message.error("Please select FY, Month/Year, Tax Regime, and Surcharge & Cess.");
                                return;
                            }
                            if (!bulkEarningHeadIds.length) {
                                message.error("Please choose at least one earning head.");
                                return;
                            }

                            setBulkLoading(true);
                            try {
                                const ym = bulkMonth; // "YYYY-MM"

                                // We'll build per-employee slabs using the same modal logic (age/gender/regime)
                                const targets = (salaryStructures || []).filter((ss: any) =>
                                    overlapsMonth(ss.start_date, ss.end_date, ym)
                                );

                                if (!targets.length) {
                                    message.warning("No employees have a salary structure in the selected month.");
                                    setBulkLoading(false);
                                    return;
                                }

                                const selectedRegime =
                                    (regimes || []).find((r: any) => r.TaxRegimeID === bulkRegimeId) || null;

                                const selectedCess =
                                    (surchargeCessList || []).find((s: any) => s.SurchargeCessID === bulkCessId) || null;

                                const allowedIds = new Set(bulkEarningHeadIds);
                                const allowedNames = new Set(
                                    (earnings || [])
                                        .filter((h: any) => allowedIds.has(Number(h.Id)))
                                        .map((h: any) => String(h.Name || "").toLowerCase())
                                );

                                const results = await Promise.allSettled(
                                    targets.map(async (ss: any) => {
                                        // 1) Employee context (age/gender)
                                        const emp = (employees || []).find(
                                            (e: any) => String(e.EmployeeID) === String(ss.employee_id)
                                        );
                                        const employeeAge = calcAgeYears(emp?.DateOfBirth, ym + "-01");
                                        const employeeGender = genderFromId(emp?.GenderId);

                                        // 2) Filter taxes by regime + age + gender
                                        const regimeEmployeeTaxes = filterTaxesForEmployee(
                                            taxes || [],
                                            bulkRegimeId,
                                            employeeAge,
                                            employeeGender
                                        );

                                        // No slabs at all for this employee
                                        if (!regimeEmployeeTaxes.length) {
                                            return { skippedNoSlabs: true, employee_id: ss.employee_id };
                                        }

                                        // 3) Build slabs like the modal
                                        const activeSlabs = buildSlabsFromTaxes(regimeEmployeeTaxes);

                                        const earningRows = Array.isArray(ss.earnings) ? ss.earnings : [];
                                        const filteredRows = earningRows.filter((e: any) =>
                                            allowedIds.has(Number(e.earning_id)) ||
                                            allowedNames.has(String(e.earning_name || "").toLowerCase())
                                        );

                                        if (!filteredRows.length) {
                                            return { skippedNoMatch: true, employee_id: ss.employee_id };
                                        }

                                        const selectedIds = filteredRows.map((e: any) => e.id);
                                        const monthlyIncome = filteredRows.reduce(
                                            (acc: number, e: any) => acc + Number(e.amount || 0),
                                            0
                                        );
                                        const annualIncome = monthlyIncome * 12;

                                        // 5) Find matching tax slab (using regimeEmployeeTaxes, not all taxes)
                                        const match = regimeEmployeeTaxes.find(
                                            (t: any) => annualIncome >= t._min && annualIncome <= t._max
                                        );
                                        const taxId = match?.tax_id ?? null;

                                        // 6) Chapter VI-A approved declarations (modal parity)
                                        const canUseVIADeductions = selectedRegime?.AllowsChapterVI_ADeductions
                                            ? Number(selectedRegime.AllowsChapterVI_ADeductions) === 1
                                            : false;

                                        const approvedDecl = (declarations || []).filter((d: any) => {
                                            return (
                                                String(d.EmployeeID) === String(ss.employee_id) &&
                                                String(d.FinancialYearID) === String(bulkFY) &&
                                                String(d.VerificationStatus || "").toLowerCase() === "approved"
                                            );
                                        });

                                        const additionalDeductionsAnnual = canUseVIADeductions
                                            ? approvedDecl.reduce((sum: number, d: any) => sum + parseNum(d.VerifiedAmount), 0)
                                            : 0;

                                        // 7) Compute monthly TDS exactly like the modal
                                        let monthlyTDS = 0;
                                        let taxableIncome = 0;
                                        if (config?.country === "Ghana") {
                                            monthlyTDS = Number((calculateMonthlyGhanaPAYE(annualIncome) || 0).toFixed(2));
                                        } else {
                                            const result:any = calculateMonthlyTDSDynamic({
                                                annualIncome,
                                                slabs: activeSlabs,
                                                regime: selectedRegime,
                                                surchargeCess: selectedCess,
                                                additionalDeductionsAnnual,
                                            });
                                            monthlyTDS = result.monthlyTDS;
                                            taxableIncome = result.taxableIncome;
                                        }

                                        if (!(monthlyTDS > 0)) {
                                            return { skippedNoTax: true, employee_id: ss.employee_id };
                                        }

                                        const payload = {
                                            EmployeeID: String(ss.employee_id),
                                            PayScaleID: ss.pay_scale_id,
                                            FinancialYearID: bulkFY,
                                            MonthYear: ym,
                                            TaxID: taxId,
                                            Selected_Earning_IDs: selectedIds,
                                            TaxRegimeID: bulkRegimeId,
                                            SurchargeCessID: bulkCessId,
                                            ApplicableIncome: monthlyIncome * 12,
                                            CalculatedTax: Number(monthlyTDS.toFixed(2)),
                                            CreatedBy: user?.UserId || 1,
                                            ModifiedBy: user?.UserId || 1,
                                            ActualIncome:taxableIncome,
                                        };

                                        return await dispatch(createEmployeeTdsContribution(payload)).unwrap();
                                    })
                                );

                                const created = results.filter(
                                    (r: any) => r.status === "fulfilled" && !(r.value && (r.value.skippedNoSlabs || r.value.skippedNoMatch || r.value.skippedNoTax))
                                ).length;

                                const skippedNoSlabs = results.filter(
                                    (r: any) => r.status === "fulfilled" && r.value?.skippedNoSlabs
                                ).length;

                                const skippedNoMatch = results.filter(
                                    (r: any) => r.status === "fulfilled" && r.value?.skippedNoMatch
                                ).length;

                                const skippedNoTax = results.filter(
                                    (r: any) => r.status === "fulfilled" && r.value?.skippedNoTax
                                ).length;

                                const failed = results.filter((r: any) => r.status === "rejected").length;

                                if (created) message.success(`TDS processed: ${created} created`);
                                if (skippedNoSlabs) message.info(`${skippedNoSlabs} skipped (no applicable slabs for regime/age/gender)`);
                                if (skippedNoMatch) message.info(`${skippedNoMatch} skipped (no matching earning heads)`);
                                if (skippedNoTax) message.info(`${skippedNoTax} skipped (Calculated Tax ≤ 0)`);
                                if (failed) message.warning(`${failed} failed`);

                                await dispatch(fetchEmployeeTdsContributions());
                                setBulkOpen(false);
                            } catch {
                                message.error("Bulk processing failed");
                            } finally {
                                setBulkLoading(false);
                            }
                        }}
                    >
                        Process TDS
                    </Button>
                </div>
            </Drawer>
        </>
    );
};

export default EmployeeTdsContribution;
